// Patent claims exercised: P-006 Claims 1-3 (AI fitness planning with health constraints)
import { API_BASE } from "../../config";
import React, { useState, useEffect } from 'react';
import MedicalDisclaimer from '../MedicalDisclaimer';
import { handleAIError } from '../../utils/aiErrorHandler';
import CostChip from '../CostChip';
import { useCreditSpend } from '../../contexts/CreditSpendContext';

const FitnessProgramGenerator = ({ user, token, fetchWithAuth, onClose, onBack, onSuccess, onPromptData }) => {
  // Auth-aware fetch with 401 retry (falls back to raw fetch if fetchWithAuth not provided)
  const authFetch = fetchWithAuth || ((url, opts) => fetch(url, { ...opts, headers: { ...opts?.headers, 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }));
  const { spend } = useCreditSpend();  // #77 fitness_program = 12 credits → always confirms
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [userProfile, setUserProfile] = useState(null);

  const [formData, setFormData] = useState({
    program_name: '',
    duration_weeks: 8,
    primary_goal: 'general_fitness',
    secondary_goals: [],
    fitness_level: 'intermediate',
    sessions_per_week: 4,
    session_duration_minutes: 60,
    equipment: [],
    include_cardio: true,
    include_strength: true,
    include_flexibility: true,
    prefer_videos_only: true
  });

  // Load user profile on mount
  useEffect(() => {
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    try {
      const response = await authFetch(`${API_BASE}/api/profile/body-characteristics`);
      if (response.ok) {
        const result = await response.json();
        const data = result.body_characteristics;
        if (data) {
          setUserProfile(data);
          // Auto-set fitness level based on exercise levels
          const cardio = data.cardio_exercise_level || 0;
          const weights = data.weight_training_level || 0;
          const combined = cardio + weights;
          
          let fitnessLevel = 'intermediate';
          if (combined <= 2) fitnessLevel = 'beginner';
          else if (combined >= 7) fitnessLevel = 'advanced';
          
          setFormData(prev => ({ 
            ...prev, 
            fitness_level: fitnessLevel 
          }));
        }
      }
    } catch (error) {
      console.error('Failed to load user profile:', error);
    }
  };

  const handleGenerate = async () => {
    if (!formData.program_name.trim()) {
      alert('Please enter a program name');
      return;
    }

    if (formData.equipment.length === 0) {
      alert('Please select at least one equipment preference (or "No Preference")');
      return;
    }

    setLoading(true);
    setLoadingProgress(0);
    setProgressMessage('Starting generation...');

    try {
      const response = await authFetch(`${API_BASE}/api/fitness-programs/generate-progressive`, {
        method: 'POST',
        body: JSON.stringify(formData)
      });

      if (await handleAIError(response)) {
        return;
      }

      if (!response.ok) {
        // B-CREDIT1 (2026-10-28): the generation limits refuse with a plain-language detail.message
        // (409 already running · 429 failure limit · 503 platform paused). Show it, not a status code.
        let msg = `HTTP error! status: ${response.status}`;
        try {
          const body = await response.json();
          const d = body?.detail;
          if (d && typeof d === 'object' && d.message) msg = d.message;
          else if (typeof d === 'string') msg = d;
        } catch (_) { /* non-JSON error body: keep the status message */ }
        throw new Error(msg);
      }

      // Read SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        
        // Process complete SSE messages
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            // Parse in its own try. The server's {"type":"error"} event used to be thrown INSIDE this
            // try, so its own catch swallowed it as a "parse error" and the form silently reset with
            // no message (Dev audit F-9, 2026-10-19). Events are now handled outside it, so an error
            // event reaches the outer catch and the user is told.
            let data;
            try {
              data = JSON.parse(line.slice(6));
            } catch (parseError) {
              console.error('Error parsing SSE data:', parseError);
              continue;
            }
            {
              if (data.type === 'prompt_data') {
                if (onPromptData) onPromptData(data.prompt_data);
              } else if (data.type === 'progress') {
                setLoadingProgress(data.progress_percentage || 0);
                if (data.message) {
                  setProgressMessage(data.message);
                }
               } else if (data.type === 'complete') {
                setLoadingProgress(100);
                setProgressMessage('Complete!');

                // Brief pause to show 100%
                await new Promise(resolve => setTimeout(resolve, 500));

                alert(`Fitness program generated successfully!\n\n` +
                      `Program: ${data.program_name}\n` +
                      `Duration: ${formData.duration_weeks} weeks`);

                setLoading(false);
                if (onSuccess) {
                  onSuccess();
                }
                return;
              } else if (data.type === 'error') {
                throw new Error(data.message || 'The fitness program could not be generated.');
              }
            }
          }
        }
      }
      
    } catch (error) {
      console.error('Generation error:', error);
      alert(`Failed to generate: ${error.message}`);
    } finally {
      setLoading(false);
      setLoadingProgress(0);
    }
  };

  const toggleEquipment = (eq) => {
    // If selecting "No Preference", clear other selections
    if (eq === 'no_preference') {
      setFormData(prev => ({
        ...prev,
        equipment: prev.equipment.includes('no_preference') ? [] : ['no_preference']
      }));
      return;
    }

    // If selecting specific equipment, remove "No Preference" if present
    setFormData(prev => ({
      ...prev,
      equipment: prev.equipment.includes(eq)
        ? prev.equipment.filter(e => e !== eq)
        : [...prev.equipment.filter(e => e !== 'no_preference'), eq]
    }));
  };

  const toggleSecondaryGoal = (goal) => {
    if (goal === formData.primary_goal) return; // Can't be both primary and secondary
    
    setFormData(prev => ({
      ...prev,
      secondary_goals: prev.secondary_goals.includes(goal)
        ? prev.secondary_goals.filter(g => g !== goal)
        : prev.secondary_goals.length < 2 
          ? [...prev.secondary_goals, goal]
          : prev.secondary_goals // Max 2 secondary goals
    }));
  };

  const goalOptions = [
    { value: 'weight_loss', label: 'Weight Loss', icon: '', description: 'Focus on burning calories and fat' },
    { value: 'muscle_gain', label: 'Muscle Gain', icon: '', description: 'Build strength and muscle mass' },
    { value: 'endurance', label: 'Endurance', icon: '', description: 'Improve cardiovascular fitness' },
    { value: 'general_fitness', label: 'General Fitness', icon: '', description: 'Overall health and wellness' },
    { value: 'athletic_performance', label: 'Athletic Performance', icon: '', description: 'Sports-specific training' },
    { value: 'body_recomposition', label: 'Body Recomp', icon: '', description: 'Lose fat while building muscle' }
  ];

  const equipmentOptions = [
    { value: 'no_preference', label: 'No Preference', icon: '', description: 'Use any available equipment' },
    { value: 'dumbbells', label: 'Dumbbells', icon: '', description: '387 exercises available' },
    { value: 'barbell', label: 'Barbell', icon: '', description: '260 exercises available' },
    { value: 'cables', label: 'Cables', icon: '', description: '254 exercises available' },
    { value: 'machines', label: 'Machines', icon: '', description: '186 exercises available' },
    { value: 'bodyweight', label: 'Bodyweight', icon: '', description: '557 exercises available' },
    { value: 'kettlebell', label: 'Kettlebell', icon: '', description: '93 exercises available' },
    { value: 'resistance_bands', label: 'Resistance Bands', icon: '', description: 'Home workout friendly' }
  ];

  // Loading Modal with Progress Bar
  if (loading) {
    return (
      <>
        <style>
          {`
            @keyframes progressStripes {
              0% { background-position: 0 0; }
              100% { background-position: 40px 0; }
            }
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.7; }
            }
          `}
        </style>
        <div className="fpg-loadingModalOverlay">
          <div className="fpg-loadingModalContent">
            {/* Animated Icon */}
            <div className="fpg-loadingIcon">
            </div>

            <h3 className="fpg-loadingTitle">
              Generating Your Fitness Program
            </h3>
            
            {/* Progress Bar Container */}
            <div className="fpg-progressBarContainer">
              {/* Animated Progress Bar with Stripes */}
              <div style={{
                width: `${loadingProgress}%`,
                height: '100%',
                backgroundColor: 'var(--brand-primary)',
                backgroundImage: 'linear-gradient(45deg, rgba(255,255,255,0.2) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.2) 75%, transparent 75%, transparent)',
                backgroundSize: '40px 40px',
                animation: 'progressStripes 1s linear infinite',
                transition: 'width 0.4s ease-out',
                borderRadius: '14px',
                boxShadow: '0 2px 8px rgba(30, 58, 95, 0.3)'
              }} />
            </div>
            
            {/* Progress Percentage */}
            <div className="fpg-progressPercentage">
              {loadingProgress}%
            </div>
            
            {/* Dynamic Progress Message from Backend */}
            <div className="fpg-progressMessage">
              {progressMessage}
            </div>
            
            {/* Status Indicator Dots */}
            <div className="fpg-statusDots">
              {[0, 1, 2].map((i) => (
                <div key={i} className="fpg-statusDot" style={{ animationDelay: `${i * 0.3}s` }} />
              ))}
            </div>
            
            <p className="fpg-loadingDescription">
              {`Creating your personalized ${formData.duration_weeks}-week fitness program with exercises and workout schedule`}
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="fpg-modal">
      <div className="fpg-modalContent">
        {/* Header */}
        <div className="fpg-header">
          <div className="fpg-headerTitle">
            <div>
              <h2 className="fpg-title">
                AI Fitness Program Generator <CostChip apiType="fitness_program" />
              </h2>
              <p className="fpg-subtitle">
                Create a personalized workout plan based on your goals
              </p>
            </div>
            <button
              onClick={onBack || onClose}
              className="fpg-closeButton"
            >
              ×
            </button>
          </div>
        </div>

        {/* User Profile Preview */}
        {userProfile && (
          <div className="fpg-profilePreview">
            <strong className="fpg-profileLabel">
              Based on your profile:
            </strong>
            <div className="fpg-profileData">
              {userProfile.age && <span>Age: {userProfile.age}</span>}
              {userProfile.weight && (
                <span>Weight: {userProfile.weight} {userProfile.weight_unit || 'lbs'}</span>
              )}
              {(userProfile.cardio_exercise_level !== undefined || userProfile.weight_training_level !== undefined) && (
                <span>Exercise: Cardio {userProfile.cardio_exercise_level || 0}/5, Weights {userProfile.weight_training_level || 0}/5</span>
              )}
            </div>
            <p className="fpg-profileDescription">
              AI will optimize your program based on this data
            </p>
          </div>
        )}

        {/* Form Content */}
        <div className="fpg-formContent">
          {/* Program Name */}
          <div className="fpg-programNameGroup">
            <label className="fpg-programNameLabel">
              Program Name *
            </label>
            <input
              type="text"
              value={formData.program_name}
              onChange={(e) => setFormData({ ...formData, program_name: e.target.value })}
              placeholder="e.g., My 8-Week Transformation"
              className="fpg-programNameInput"
            />
          </div>

          {/* Primary Goal Selection */}
          <div className="fpg-primaryGoalGroup">
            <label className="fpg-primaryGoalLabel">
              Primary Goal *
            </label>
            <div className="fpg-goalOptions">
              {goalOptions.map(goal => (
                <div
                  key={goal.value}
                  onClick={() => setFormData({ 
                    ...formData, 
                    primary_goal: goal.value,
                    secondary_goals: formData.secondary_goals.filter(g => g !== goal.value)
                  })}
                  className="fpg-goalCard" style={{ border: `2px solid ${formData.primary_goal === goal.value ? '#1E3A5F' : '#E5E7EB'}`, backgroundColor: formData.primary_goal === goal.value ? '#EBF0F7' : 'white' }}
                >
                  <div className="fpg-goalIcon">{goal.icon}</div>
                  <div className="fpg-goalLabel">{goal.label}</div>
                  <div className="fpg-goalDescription">{goal.description}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Equipment Preferences */}
          <div className="fpg-equipmentGroup">
            <label className="fpg-equipmentLabel">
              Equipment Preferences * <span className="fpg-equipmentHelper">(select all available)</span>
            </label>
            <div className="fpg-equipmentOptions">
              {equipmentOptions.map(eq => (
                <div
                  key={eq.value}
                  onClick={() => toggleEquipment(eq.value)}
                  className="fpg-equipmentCard" style={{ border: `2px solid ${formData.equipment.includes(eq.value) ? '#1E3A5F' : '#E5E7EB'}`, backgroundColor: formData.equipment.includes(eq.value) ? '#EBF0F7' : 'white' }}
                >
                  <div className="fpg-equipmentIcon">{eq.icon}</div>
                  <div className="fpg-equipmentCardLabel">{eq.label}</div>
                  <div className="fpg-equipmentDescription">
                    {eq.description}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Duration, Sessions, Fitness Level */}
          <div className="fpg-configRow">
            <div>
              <label className="fpg-durationLabel">
                Duration
              </label>
              <select
                value={formData.duration_weeks}
                onChange={(e) => setFormData({ ...formData, duration_weeks: parseInt(e.target.value) })}
                className="fpg-durationSelect"
              >
                <option value={4}>4 weeks</option>
                <option value={6}>6 weeks</option>
                <option value={8}>8 weeks</option>
                <option value={12}>12 weeks</option>
                <option value={16}>16 weeks</option>
              </select>
            </div>
            <div>
              <label className="fpg-sessionsLabel">
                Sessions/Week
              </label>
              <select
                value={formData.sessions_per_week}
                onChange={(e) => setFormData({ ...formData, sessions_per_week: parseInt(e.target.value) })}
                className="fpg-sessionsSelect"
              >
                <option value={3}>3 days/week</option>
                <option value={4}>4 days/week</option>
                <option value={5}>5 days/week</option>
                <option value={6}>6 days/week</option>
              </select>
            </div>
            <div>
              <label className="fpg-fitnessLevelLabel">
                Fitness Level
              </label>
              <select
                value={formData.fitness_level}
                onChange={(e) => setFormData({ ...formData, fitness_level: e.target.value })}
                className="fpg-fitnessLevelSelect"
              >
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>
          </div>

          {/* Session Duration */}
          <div className="fpg-sessionDurationGroup">
            <label className="fpg-sessionDurationLabel">
              Session Duration
            </label>
            <div className="fpg-durationOptions">
              {[30, 45, 60, 75, 90].map(mins => (
                <button
                  key={mins}
                  onClick={() => setFormData({ ...formData, session_duration_minutes: mins })}
                  className="fpg-durationButton" style={{ border: `2px solid ${formData.session_duration_minutes === mins ? '#1E3A5F' : '#E5E7EB'}`, backgroundColor: formData.session_duration_minutes === mins ? '#EBF0F7' : 'white' }}
                >
                  {mins} min
                </button>
              ))}
            </div>
          </div>

          {/* Include Options */}
          <div className="fpg-includeGroup">
            <label className="fpg-includeLabel">
              Include in Program
            </label>
            <div className="fpg-includeOptions">
              <label className="fpg-strengthCheckbox" style={{ border: `2px solid ${formData.include_strength ? '#1E3A5F' : '#E5E7EB'}`, backgroundColor: formData.include_strength ? '#EBF0F7' : 'white' }}>
                <input
                  type="checkbox"
                  checked={formData.include_strength}
                  onChange={(e) => setFormData({ ...formData, include_strength: e.target.checked })}
                />
                Strength Training
              </label>
              <label className="fpg-cardioCheckbox" style={{ border: `2px solid ${formData.include_cardio ? '#1E3A5F' : '#E5E7EB'}`, backgroundColor: formData.include_cardio ? '#EBF0F7' : 'white' }}>
                <input
                  type="checkbox"
                  checked={formData.include_cardio}
                  onChange={(e) => setFormData({ ...formData, include_cardio: e.target.checked })}
                />
                Cardio
              </label>
              <label className="fpg-flexibilityCheckbox" style={{ border: `2px solid ${formData.include_flexibility ? '#1E3A5F' : '#E5E7EB'}`, backgroundColor: formData.include_flexibility ? '#EBF0F7' : 'white' }}>
                <input
                  type="checkbox"
                  checked={formData.include_flexibility}
                  onChange={(e) => setFormData({ ...formData, include_flexibility: e.target.checked })}
                />
                Flexibility
              </label>
            </div>
          </div>

          {/* AI Options */}
          <div className="fpg-aiOptionsGroup">
            <label className="fpg-aiOptionsLabel">
              AI Generation Options
            </label>
            
            <div className="fpg-aiOptionsContent">
              {/* Prefer Videos Checkbox */}
              <label className="fpg-preferVideosCheckbox" style={{ border: `2px solid ${formData.prefer_videos_only ? '#1E3A5F' : '#E5E7EB'}`, backgroundColor: formData.prefer_videos_only ? '#EBF0F7' : 'white' }}>
                <input
                  type="checkbox"
                  checked={formData.prefer_videos_only}
                  onChange={(e) => setFormData({ ...formData, prefer_videos_only: e.target.checked })}
                  className="fpg-preferVideosInput"
                />
                <div>
                  <span className="fpg-preferVideosText">Prefer exercises with instructional videos</span>
                  <p className="fpg-preferVideosDescription">
                    Only include exercises that have video demonstrations (recommended)
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Secondary Goals (Optional) */}
          <div className="fpg-secondaryGoalsGroup">
            <label className="fpg-secondaryGoalsLabel">
              Secondary Goals <span className="fpg-secondaryGoalsHelper">(optional, max 2)</span>
            </label>
            <div className="fpg-secondaryGoalButtons">
              {goalOptions
                .filter(g => g.value !== formData.primary_goal)
                .map(goal => (
                  <button
                    key={goal.value}
                    onClick={() => toggleSecondaryGoal(goal.value)}
                    className="fpg-secondaryGoalButton" style={{ border: `2px solid ${formData.secondary_goals.includes(goal.value) ? '#1E3A5F' : '#E5E7EB'}`, backgroundColor: formData.secondary_goals.includes(goal.value) ? '#EBF0F7' : 'white' }}
                  >
                    {goal.icon} {goal.label}
                  </button>
                ))}
            </div>
          </div>

          {/* Generate Button */}
          <button
            onClick={() => spend('fitness_program', 'Generate this fitness program?', handleGenerate)}
            disabled={!formData.program_name.trim() || formData.equipment.length === 0}
            className="fpg-generateButton" style={{ backgroundColor: (!formData.program_name.trim() || formData.equipment.length === 0)
                ? '#D1D5DB'
                : 'linear-gradient(135deg, #1E3A5F 0%, #2D4F7A 100%)', background: (!formData.program_name.trim() || formData.equipment.length === 0)
                ? '#D1D5DB'
                : 'linear-gradient(135deg, #1E3A5F 0%, #2D4F7A 100%)', cursor: (!formData.program_name.trim() || formData.equipment.length === 0)
                ? 'not-allowed'
                : 'pointer' }}
          >
            Generate My Fitness Program <CostChip apiType="fitness_program" />
          </button>

          <p className="fpg-generateDescription">
            AI will create a personalized {formData.duration_weeks}-week program with exercise videos
          </p>

          <MedicalDisclaimer variant="ai" />
        </div>
      </div>
    </div>
  );
};

export default FitnessProgramGenerator;